// 2026-11-02: Admin > People > an account > Spend: the shape GET /api/admin/people/:id/spend returns, checked (pure;
// tested in personspend.test.ts), and the small sums the page draws with.
export type SpendWindow = "7d" | "30d" | "90d" | "all";
export const SPEND_WINDOWS: { id: SpendWindow; label: string }[] = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
  { id: "all", label: "All time" },
];
export type SpendKind = "frame" | "clip" | "voice" | "music" | "sfx" | "cues" | "render" | "script" | "director" | "other";
export const KIND_LABEL: Record<SpendKind, string> = { frame: "Pictures", clip: "Clips", voice: "Voices", music: "Music", sfx: "Sound effects", cues: "Sound cues", render: "Renders", script: "Scripts", director: "Director calls", other: "Other" };

export type PersonSpend = {
  window: SpendWindow;
  since: string | null;
  totals: { costUsd: number; chargedCredits: number; chargedUsd: number; marginUsd: number; markup: number | null; boughtCredits: number; purchases: number; givenCredits: number; balance: number };
  byKind: { kind: SpendKind; costUsd: number; chargedCredits: number; chargedUsd: number; markup: number | null; calls: number }[];
  byModel: { provider: string; model: string; kind: SpendKind; costUsd: number; calls: number; failed: number }[];
  byProject: { projectId: string | null; title: string; costUsd: number; chargedCredits: number }[];
  director: { calls: number; minutes: number; costUsd: number; chargedCredits: number };
  daily: { day: string; costUsd: number; chargedUsd: number }[];
  recent: { at: string; kind: string; label: string; project: string | null; credits: number; costUsd: number | null }[];
};

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});
const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const nOrNull = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const s = (v: unknown) => (typeof v === "string" ? v : "");
const arr = (v: unknown) => (Array.isArray(v) ? v.map(obj) : []);
const KINDS = Object.keys(KIND_LABEL) as SpendKind[];
const kind = (v: unknown): SpendKind => (KINDS.includes(v as SpendKind) ? (v as SpendKind) : "other");

export function normPersonSpend(raw: unknown): PersonSpend {
  const o = obj(raw);
  const t = obj(o.totals);
  const dr = obj(o.director);
  return {
    window: (["7d", "30d", "90d", "all"].includes(o.window as string) ? o.window : "30d") as SpendWindow,
    since: typeof o.since === "string" ? o.since : null,
    totals: { costUsd: n(t.costUsd), chargedCredits: n(t.chargedCredits), chargedUsd: n(t.chargedUsd), marginUsd: n(t.marginUsd), markup: nOrNull(t.markup), boughtCredits: n(t.boughtCredits), purchases: n(t.purchases), givenCredits: n(t.givenCredits), balance: n(t.balance) },
    byKind: arr(o.byKind).map((k) => ({ kind: kind(k.kind), costUsd: n(k.costUsd), chargedCredits: n(k.chargedCredits), chargedUsd: n(k.chargedUsd), markup: nOrNull(k.markup), calls: n(k.calls) })),
    byModel: arr(o.byModel).map((m) => ({ provider: s(m.provider), model: s(m.model), kind: kind(m.kind), costUsd: n(m.costUsd), calls: n(m.calls), failed: n(m.failed) })),
    byProject: arr(o.byProject).map((p) => ({ projectId: typeof p.projectId === "string" ? p.projectId : null, title: s(p.title) || "Untitled", costUsd: n(p.costUsd), chargedCredits: n(p.chargedCredits) })),
    director: { calls: n(dr.calls), minutes: n(dr.minutes), costUsd: n(dr.costUsd), chargedCredits: n(dr.chargedCredits) },
    daily: arr(o.daily).map((d) => ({ day: s(d.day), costUsd: n(d.costUsd), chargedUsd: n(d.chargedUsd) })),
    recent: arr(o.recent).map((r) => ({ at: s(r.at), kind: s(r.kind), label: s(r.label), project: typeof r.project === "string" ? r.project : null, credits: n(r.credits), costUsd: nOrNull(r.costUsd) })),
  };
}

/** Each kind's share of the larger of cost and charge across kinds (0..1), so cost and charge bars share one scale. */
export function kindScale(rows: PersonSpend["byKind"]): number {
  return Math.max(0.01, ...rows.map((r) => Math.max(r.costUsd, r.chargedUsd)));
}

/** The daily chart's top (a round number a bit above the busiest day), and the days that had anything. */
export function chartTop(days: PersonSpend["daily"]): number {
  const max = Math.max(0, ...days.map((d) => Math.max(d.costUsd, d.chargedUsd)));
  if (max <= 0) return 1;
  const step = 10 ** Math.floor(Math.log10(max));
  return Math.ceil((max * 1.05) / step) * step;
}

/** The kinds with the most money in them first (cost or charge). */
export function sortKinds(rows: PersonSpend["byKind"]): PersonSpend["byKind"] {
  return [...rows].sort((a, b) => Math.max(b.costUsd, b.chargedUsd) - Math.max(a.costUsd, a.chargedUsd));
}

export const usd = (v: number) =>
  v.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: Math.abs(v) > 0 && Math.abs(v) < 0.1 ? 3 : 2 });
export const markupText = (m: number | null) => (m == null ? "not charged" : `${m.toFixed(1)}×`);
